/**
 * Fee Comparison Color Utilities
 *
 * Helper functions for computing color classes based on fee comparison state.
 * These functions reduce cognitive complexity by extracting conditional styling logic.
 * @module utils/feeComparisonColors
 */

import { ColorClass } from "./colorScale";

/** Theme type for color function parameters */
export type Theme = "dark" | "light";

/** Lookup object for neutral fee colors by theme */
const NEUTRAL_FEE_COLORS: Record<Theme, ColorClass> = {
  dark: {
    background: "#27272A",
    text: "#A1A1AA",
    border: "#3F3F46",
  },
  light: {
    background: "#F4F4F5",
    text: "#71717A",
    border: "#D4D4D8",
  },
} as const;

/** Lookup object for overpriced colors by theme */
const OVERPRICED_COLORS: Record<Theme, ColorClass> = {
  dark: {
    background: "#7F1D1D80",
    text: "#F87171",
    border: "#991B1B",
  },
  light: {
    background: "#FEE2E2",
    text: "#DC2626",
    border: "#F87171",
  },
} as const;

/** Lookup object for underpriced colors by theme */
const UNDERPRICED_COLORS: Record<Theme, ColorClass> = {
  dark: {
    background: "#14532D80",
    text: "#4ADE80",
    border: "#166534",
  },
  light: {
    background: "#DCFCE7",
    text: "#16A34A",
    border: "#4ADE80",
  },
} as const;

/** Lookup object for fair price colors by theme */
const FAIR_PRICE_COLORS: Record<Theme, ColorClass> = {
  dark: {
    background: "#27272A80",
    text: "#D4D4D8",
    border: "#404040",
  },
  light: {
    background: "#F9FAFB",
    text: "#374151",
    border: "#D1D5DB",
  },
} as const;

/**
 * Get color class for neutral/unknown fee state
 * @param theme - The color theme ("dark" or "light")
 * @returns Color class for neutral state
 */
export function getNeutralFeeColors(theme: Theme): ColorClass {
  return NEUTRAL_FEE_COLORS[theme];
}

/**
 * Get color class for overpriced fee state (expected > fair by >15%)
 * @param theme - The color theme ("dark" or "light")
 * @returns Color class for overpriced state
 */
export function getOverpricedColors(theme: Theme): ColorClass {
  return OVERPRICED_COLORS[theme];
}

/**
 * Get color class for underpriced fee state (expected < fair by >15%)
 * @param theme - The color theme ("dark" or "light")
 * @returns Color class for underpriced state
 */
export function getUnderpricedColors(theme: Theme): ColorClass {
  return UNDERPRICED_COLORS[theme];
}

/**
 * Get color class for fair price state (within 15% of fair value)
 * @param theme - The color theme ("dark" or "light")
 * @returns Color class for fair price state
 */
export function getFairPriceColors(theme: Theme): ColorClass {
  return FAIR_PRICE_COLORS[theme];
}

/**
 * Determine fee comparison state based on percent difference
 * @param percentDiff - Percentage difference between expected and fair fee
 * @returns Fee comparison state
 */
export function getFeeComparisonState(
  percentDiff: number
): "overpriced" | "underpriced" | "fair" {
  if (percentDiff > 15) {
    return "overpriced";
  }
  if (percentDiff < -15) {
    return "underpriced";
  }
  return "fair";
}
